import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppNav } from "@/components/AppNav";
import { BrBox } from "@/components/BrBox";
import { BrBtn } from "@/components/BrBtn";
import { BrLabel } from "@/components/BrLabel";
import { BrRangeSlider } from "@/components/BrRangeSlider";
import {
  useAllGames,
  useMe,
  useResync,
  useStats,
  offsetShadow,
  TYPE,
  formatDate,
  formatTimeControl,
  initialsFrom,
  openingLabel,
  resultLetter,
  timeControlSeconds,
  type GameOut,
} from "@chess/shared";
import { useToast } from "@/components/ToastProvider";
import { ProviderIcon } from "@/components/ProviderIcon";
import { useTheme } from "@/theme/ThemeProvider";
import { useViewport } from "@/util/viewport";
import { ChevronLeft, ChevronRight } from "lucide-react";

interface Props {
  onOpenSettings: () => void;
}

const PAGE_SIZE = 50;
// RES | OPPONENT·OPENING | ICON | TIME | MOVES | MISTAKES | ACCURACY | DATE | →
const GAMES_GRID = "44px 1fr 20px 110px 72px 92px 92px 76px 36px";

// Em-dash when unknown (game not evaluated).
function fmtMistakes(mine: number | null): string {
  return mine != null ? `${mine}` : "–";
}

function fmtAcc(mine: number | null): string {
  return mine != null ? `${Math.round(mine)}%` : "–";
}

// The viewer's own mistakes/accuracy for a game (their side).
function myMistakesOf(g: GameOut): number | null {
  return g.color === "white" ? g.white_mistakes : g.black_mistakes;
}
function myAccuracyOf(g: GameOut): number | null {
  return g.color === "white" ? g.white_accuracy : g.black_accuracy;
}

type SortKey = "date" | "accuracy" | "mistakes" | "time" | "moves";
type SortDir = "asc" | "desc";

export function ProfilePage({ onOpenSettings }: Props) {
  const p = useTheme();
  const { isMobile } = useViewport();
  const nav = useNavigate();
  const { data: me } = useMe();
  const { data: stats } = useStats();
  const resync = useResync();
  const { push } = useToast();

  const onResync = () => {
    resync.mutate(undefined, {
      onSuccess: (r) =>
        push({
          kind: r.triggered > 0 ? "success" : "info",
          text:
            r.triggered > 0
              ? "Re-syncing your games — new ones appear in a few minutes."
              : "No connected accounts to sync.",
        }),
      onError: () => push({ kind: "danger", text: "Could not start resync. Try again." }),
    });
  };

  const gamesQ = useAllGames();
  const allGames = useMemo(() => gamesQ.data ?? [], [gamesQ.data]);

  // ---- Derived filter option lists (from the games the user actually has) ----
  const providers = useMemo(
    () => Array.from(new Set(allGames.map((g) => g.provider))).sort(),
    [allGames],
  );
  const timeFormats = useMemo(
    () =>
      Array.from(new Set(allGames.map((g) => g.time_control).filter((t): t is string => !!t))).sort(
        (a, b) => timeControlSeconds(a) - timeControlSeconds(b),
      ),
    [allGames],
  );
  const openings = useMemo(
    () => Array.from(new Set(allGames.map((g) => openingLabel(g)).filter((o) => o && o !== "—"))).sort(),
    [allGames],
  );
  const opponents = useMemo(
    () =>
      Array.from(new Set(allGames.map((g) => g.opponent_username).filter((o): o is string => !!o))).sort(
        (a, b) => a.toLowerCase().localeCompare(b.toLowerCase()),
      ),
    [allGames],
  );
  const maxMistakes = useMemo(
    () => allGames.reduce((mx, g) => Math.max(mx, myMistakesOf(g) ?? 0), 0),
    [allGames],
  );
  const maxMoves = useMemo(
    () => allGames.reduce((mx, g) => Math.max(mx, g.move_count ?? 0), 0),
    [allGames],
  );

  // ---- Sort + filter state ----
  // sortKey null = no explicit sort (falls back to newest-first).
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [selProviders, setSelProviders] = useState<Set<string>>(new Set());
  const [selTimeFormat, setSelTimeFormat] = useState<string>("");
  const [selOpening, setSelOpening] = useState<string>("");
  const [selOpponent, setSelOpponent] = useState<string>("");
  const [accRange, setAccRange] = useState<[number, number]>([0, 100]);
  const [misRange, setMisRange] = useState<[number, number]>([0, 0]);
  const [movesRange, setMovesRange] = useState<[number, number]>([0, 0]);

  // Initialise the mistakes/moves-range ceilings once games load.
  useEffect(() => {
    setMisRange([0, maxMistakes]);
  }, [maxMistakes]);
  useEffect(() => {
    setMovesRange([0, maxMoves]);
  }, [maxMoves]);

  const [pageIdx, setPageIdx] = useState(0);

  function toggleProvider(prov: string) {
    setSelProviders((prev) => {
      const next = new Set(prev);
      if (next.has(prov)) next.delete(prov);
      else next.add(prov);
      return next;
    });
    setPageIdx(0);
  }

  function resetFilters() {
    setSelProviders(new Set());
    setSelTimeFormat("");
    setSelOpening("");
    setSelOpponent("");
    setAccRange([0, 100]);
    setMisRange([0, maxMistakes]);
    setMovesRange([0, maxMoves]);
    setPageIdx(0);
  }

  // ---- Apply filters then sort (all client-side, instant) ----
  const filtered = useMemo(() => {
    return allGames.filter((g) => {
      if (selProviders.size > 0 && !selProviders.has(g.provider)) return false;
      if (selTimeFormat && g.time_control !== selTimeFormat) return false;
      if (selOpening && openingLabel(g) !== selOpening) return false;
      if (selOpponent && g.opponent_username !== selOpponent) return false;
      const acc = myAccuracyOf(g);
      // Only constrain accuracy when the slider is narrowed from the full range.
      if (accRange[0] > 0 || accRange[1] < 100) {
        if (acc == null) return false;
        if (acc < accRange[0] || acc > accRange[1]) return false;
      }
      const mis = myMistakesOf(g) ?? 0;
      if (mis < misRange[0] || mis > misRange[1]) return false;
      const mv = g.move_count ?? 0;
      if (mv < movesRange[0] || mv > movesRange[1]) return false;
      return true;
    });
  }, [allGames, selProviders, selTimeFormat, selOpening, selOpponent, accRange, misRange, movesRange]);

  const sorted = useMemo(() => {
    // Default to newest-first when no explicit sort is chosen.
    const effKey: SortKey = sortKey ?? "date";
    const effDir: SortDir = sortKey ? sortDir : "desc";
    const dir = effDir === "asc" ? 1 : -1;
    const key = (g: GameOut): number => {
      switch (effKey) {
        case "date":
          return new Date(g.played_at).getTime();
        case "accuracy":
          return myAccuracyOf(g) ?? -1;
        case "mistakes":
          return myMistakesOf(g) ?? -1;
        case "time":
          return timeControlSeconds(g.time_control);
        case "moves":
          return g.move_count ?? -1;
      }
    };
    return [...filtered].sort((a, b) => (key(a) - key(b)) * dir);
  }, [filtered, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const safePage = Math.min(pageIdx, totalPages - 1);
  const visibleGames = sorted.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);
  const canPrev = safePage > 0;
  const canNext = safePage < totalPages - 1;

  function goNext() {
    if (canNext) setPageIdx(safePage + 1);
  }
  function goPrev() {
    if (canPrev) setPageIdx(safePage - 1);
  }
  // Tri-state per key: off → desc → asc → off.
  function cycleSort(key: SortKey) {
    setPageIdx(0);
    if (sortKey !== key) {
      setSortKey(key);
      setSortDir("desc");
    } else if (sortDir === "desc") {
      setSortDir("asc");
    } else {
      setSortKey(null); // back to default
    }
  }

  return (
    <div style={{ minHeight: "100vh", background: p.bg, color: p.fg, fontFamily: TYPE.body }}>
      <AppNav onOpenSettings={onOpenSettings} />
      <main
        style={{
          maxWidth: 1280,
          margin: "0 auto",
          padding: isMobile ? "20px 16px 56px" : "32px 32px 72px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 14 : 22, marginBottom: isMobile ? 24 : 32, flexWrap: "wrap" }}>
          <div
            style={{
              width: isMobile ? 72 : 96,
              height: isMobile ? 72 : 96,
              background: p.accent,
              color: p.accentFg,
              border: `3px solid ${p.border}`,
              boxShadow: offsetShadow(p, 6, 6),
              display: "grid",
              placeItems: "center",
              fontFamily: TYPE.display,
              fontSize: isMobile ? 28 : 38,
              fontWeight: 800,
              letterSpacing: -1,
              flexShrink: 0,
            }}
          >
            {initialsFrom(me?.display_name ?? me?.email)}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <BrLabel>PROFILE</BrLabel>
            <h1
              style={{
                fontFamily: TYPE.display,
                fontSize: isMobile ? 36 : 52,
                fontWeight: 800,
                letterSpacing: isMobile ? -1.2 : -2,
                lineHeight: 0.95,
                margin: "4px 0 0",
                textTransform: "uppercase",
              }}
            >
              {(me?.display_name ?? me?.email ?? "PLAYER").toUpperCase()}
            </h1>
            <div
              style={{
                display: "flex",
                gap: 14,
                marginTop: 8,
                fontSize: 12,
                fontFamily: TYPE.mono,
                fontWeight: 700,
                color: p.fgMuted,
                letterSpacing: 0.5,
                flexWrap: "wrap",
                textTransform: "uppercase",
              }}
            >
              <span>{me?.email ?? "—"}</span>
              <span>·</span>
              <span>{(me?.effective_tier ?? "free").toUpperCase()} PLAN</span>
              {(me?.connected_accounts ?? []).map((a) => {
                const c = a.state === "error" ? p.danger : a.state === "live" ? p.success : p.fgMuted;
                const dot = a.state === "error" ? "✕" : a.state === "live" ? "●" : "○";
                return (
                  <span key={a.id} title={a.sync_error ?? undefined}>
                    {a.provider}: <span style={{ color: c }}>{dot} {a.external_username}</span>
                  </span>
                );
              })}
            </div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <BrBtn
              size="sm"
              variant="accent"
              disabled={resync.isPending}
              onClick={onResync}
            >
              {resync.isPending ? "Syncing…" : "Resync games"}
            </BrBtn>
            <BrBtn size="sm" variant="surface" onClick={onOpenSettings}>Edit profile</BrBtn>
          </div>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(4, 1fr)",
            gap: isMobile ? 10 : 16,
            marginBottom: isMobile ? 24 : 32,
          }}
        >
          {[
            { l: "GAMES", v: stats?.games_count ?? "—" },
            { l: "MISTAKES", v: stats?.mistakes_count ?? "—", hi: true },
            { l: "FIXED", v: stats?.mistakes_fixed_count ?? "—" },
            { l: "ACCOUNTS", v: (me?.connected_accounts ?? []).length },
          ].map((r) => (
            <BrBox key={r.l} padding={isMobile ? 12 : 18} shadowSize={isMobile ? 3 : 4} bg={r.hi ? p.accent : p.card} fg={r.hi ? p.accentFg : p.fg}>
              <BrLabel color={r.hi ? p.accentFg : p.fgMuted}>{r.l}</BrLabel>
              <div style={{ fontFamily: TYPE.display, fontSize: isMobile ? 32 : 44, fontWeight: 800, letterSpacing: -1.5, lineHeight: 1, marginTop: 8 }}>{r.v}</div>
            </BrBox>
          ))}
        </div>

        <Section label="GAME HISTORY" title="All games" isMobile={isMobile}>
          {allGames.length > 0 && (
            <FilterBar
              p={p}
              isMobile={isMobile}
              sortKey={sortKey}
              sortDir={sortDir}
              cycleSort={cycleSort}
              filtersOpen={filtersOpen}
              setFiltersOpen={setFiltersOpen}
              providers={providers}
              selProviders={selProviders}
              toggleProvider={toggleProvider}
              timeFormats={timeFormats}
              selTimeFormat={selTimeFormat}
              setSelTimeFormat={(v) => { setSelTimeFormat(v); setPageIdx(0); }}
              openings={openings}
              selOpening={selOpening}
              setSelOpening={(v) => { setSelOpening(v); setPageIdx(0); }}
              opponents={opponents}
              selOpponent={selOpponent}
              setSelOpponent={(v) => { setSelOpponent(v); setPageIdx(0); }}
              accRange={accRange}
              setAccRange={(v) => { setAccRange(v); setPageIdx(0); }}
              misRange={misRange}
              setMisRange={(v) => { setMisRange(v); setPageIdx(0); }}
              maxMistakes={maxMistakes}
              movesRange={movesRange}
              setMovesRange={(v) => { setMovesRange(v); setPageIdx(0); }}
              maxMoves={maxMoves}
              resetFilters={resetFilters}
              shownCount={sorted.length}
              totalCount={allGames.length}
            />
          )}
          <BrBox padding={0} shadowSize={5}>
            {allGames.length === 0 && (
              <div style={{ padding: 18, fontSize: 13, color: p.fgMuted }}>
                {gamesQ.isLoading ? "Loading games…" : "No games yet — link an account from settings to import."}
              </div>
            )}
            {allGames.length > 0 && sorted.length === 0 && (
              <div style={{ padding: 18, fontSize: 13, color: p.fgMuted }}>No games match the current filters.</div>
            )}
            {!isMobile && visibleGames.length > 0 && (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: GAMES_GRID,
                  gap: 10,
                  padding: "10px 14px",
                  borderBottom: `2px solid ${p.border}`,
                  background: p.bg,
                  fontSize: 10,
                  fontFamily: TYPE.mono,
                  fontWeight: 700,
                  letterSpacing: 1,
                  textTransform: "uppercase",
                  color: p.fgMuted,
                }}
              >
                <span>RES</span>
                <span>OPPONENT · OPENING</span>
                <span />
                <span>TIME</span>
                <span style={{ textAlign: "center" }}>MOVES</span>
                <span style={{ textAlign: "center" }}>MISTAKES</span>
                <span style={{ textAlign: "center" }}>ACCURACY</span>
                <span>DATE</span>
                <span />
              </div>
            )}
            {visibleGames.map((g, i) => {
              const r = resultLetter(g.result);
              const resultBg = r === "W" ? p.success : r === "L" ? p.danger : p.bg;
              const resultFg = r === "W" ? p.successFg : r === "L" ? p.dangerFg : p.fg;
              const youWhite = g.color === "white";
              const myMistakes = youWhite ? g.white_mistakes : g.black_mistakes;
              const myAcc = youWhite ? g.white_accuracy : g.black_accuracy;
              return (
                <button
                  key={g.id}
                  onClick={() => nav(`/app/analysis/${g.id}`)}
                  style={{
                    display: isMobile ? "flex" : "grid",
                    gridTemplateColumns: GAMES_GRID,
                    gap: 10,
                    padding: "12px 14px",
                    alignItems: "center",
                    width: "100%",
                    background: "transparent",
                    color: p.fg,
                    border: "none",
                    borderBottom: i < visibleGames.length - 1 ? `2px solid ${p.border}` : "none",
                    cursor: "pointer",
                    fontFamily: TYPE.body,
                    textAlign: "left",
                  }}
                >
                  <div
                    style={{
                      width: 32,
                      height: 32,
                      background: resultBg,
                      color: resultFg,
                      border: `2px solid ${p.border}`,
                      display: "grid",
                      placeItems: "center",
                      fontSize: 13,
                      fontWeight: 800,
                      fontFamily: TYPE.mono,
                      flexShrink: 0,
                    }}
                  >
                    {r}
                  </div>
                  {isMobile ? (
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 700 }}>
                        vs <span style={{ fontFamily: TYPE.mono }}>{g.opponent_username ?? "?"}</span>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 5,
                          fontSize: 11,
                          color: p.fgMuted,
                          fontFamily: TYPE.mono,
                          fontWeight: 700,
                          marginTop: 2,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          textTransform: "uppercase",
                        }}
                      >
                        <ProviderIcon provider={g.provider} size={13} color={p.fgMuted} />
                        {formatTimeControl(g.time_control)} · {openingLabel(g)}
                      </div>
                      <div style={{ fontSize: 11, color: p.fgMuted, fontFamily: TYPE.mono, fontWeight: 700, marginTop: 2 }}>
                        {g.move_count != null ? `${g.move_count} moves · ` : ""}{fmtMistakes(myMistakes)} mistakes · {fmtAcc(myAcc)}
                      </div>
                    </div>
                  ) : (
                    <>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 13.5, fontWeight: 700 }}>
                          vs <span style={{ fontFamily: TYPE.mono }}>{g.opponent_username ?? "?"}</span>
                        </div>
                        <div
                          style={{
                            fontSize: 11,
                            color: p.fgMuted,
                            fontFamily: TYPE.mono,
                            fontWeight: 700,
                            marginTop: 2,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {openingLabel(g)}
                        </div>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <ProviderIcon provider={g.provider} size={15} color={p.fgMuted} />
                      </div>
                      <div style={{ fontSize: 11, fontFamily: TYPE.mono, fontWeight: 700, color: p.fgMuted, letterSpacing: 0.5, textTransform: "uppercase" }}>
                        {formatTimeControl(g.time_control)}
                      </div>
                      <div style={{ fontSize: 12, fontFamily: TYPE.mono, fontWeight: 800, textAlign: "center", color: p.fgMuted }}>
                        {g.move_count != null ? g.move_count : "–"}
                      </div>
                      <div style={{ fontSize: 12, fontFamily: TYPE.mono, fontWeight: 800, textAlign: "center" }}>
                        {fmtMistakes(myMistakes)}
                      </div>
                      <div style={{ fontSize: 12, fontFamily: TYPE.mono, fontWeight: 800, textAlign: "center" }}>
                        {fmtAcc(myAcc)}
                      </div>
                      <div style={{ fontSize: 11, fontFamily: TYPE.mono, fontWeight: 700, color: p.fgMuted, letterSpacing: 0.5 }}>{formatDate(g.played_at)}</div>
                      <div style={{ fontSize: 14, fontWeight: 800, textAlign: "right", color: p.fgMuted }}>→</div>
                    </>
                  )}
                </button>
              );
            })}
            {sorted.length > PAGE_SIZE && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 14,
                  padding: 14,
                  borderTop: `2px solid ${p.border}`,
                  background: p.bg,
                }}
              >
                <button
                  onClick={goPrev}
                  disabled={!canPrev}
                  aria-label="Previous page"
                  style={paginatorBtn(p, !canPrev)}
                >
                  <ChevronLeft size={18} strokeWidth={2} />
                </button>
                <div style={{ fontFamily: TYPE.mono, fontSize: 13, fontWeight: 800, letterSpacing: 1, textTransform: "uppercase", minWidth: 100, textAlign: "center" }}>
                  Page {safePage + 1} / {totalPages}
                </div>
                <button
                  onClick={goNext}
                  disabled={!canNext}
                  aria-label="Next page"
                  style={paginatorBtn(p, !canNext)}
                >
                  <ChevronRight size={18} strokeWidth={2} />
                </button>
              </div>
            )}
          </BrBox>
        </Section>
      </main>
    </div>
  );
}

function paginatorBtn(p: ReturnType<typeof useTheme>, disabled: boolean): React.CSSProperties {
  return {
    width: 36,
    height: 36,
    background: p.card,
    color: p.fg,
    border: `2px solid ${p.border}`,
    boxShadow: offsetShadow(p, 2, 2),
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.4 : 1,
    display: "grid",
    placeItems: "center",
  };
}

function FilterBar({
  p,
  isMobile,
  sortKey,
  sortDir,
  cycleSort,
  filtersOpen,
  setFiltersOpen,
  providers,
  selProviders,
  toggleProvider,
  timeFormats,
  selTimeFormat,
  setSelTimeFormat,
  openings,
  selOpening,
  setSelOpening,
  opponents,
  selOpponent,
  setSelOpponent,
  accRange,
  setAccRange,
  misRange,
  setMisRange,
  maxMistakes,
  movesRange,
  setMovesRange,
  maxMoves,
  resetFilters,
  shownCount,
  totalCount,
}: {
  p: ReturnType<typeof useTheme>;
  isMobile: boolean;
  sortKey: SortKey | null;
  sortDir: SortDir;
  cycleSort: (k: SortKey) => void;
  filtersOpen: boolean;
  setFiltersOpen: (v: boolean) => void;
  providers: string[];
  selProviders: Set<string>;
  toggleProvider: (prov: string) => void;
  timeFormats: string[];
  selTimeFormat: string;
  setSelTimeFormat: (v: string) => void;
  openings: string[];
  selOpening: string;
  setSelOpening: (v: string) => void;
  opponents: string[];
  selOpponent: string;
  setSelOpponent: (v: string) => void;
  accRange: [number, number];
  setAccRange: (v: [number, number]) => void;
  misRange: [number, number];
  setMisRange: (v: [number, number]) => void;
  maxMistakes: number;
  movesRange: [number, number];
  setMovesRange: (v: [number, number]) => void;
  maxMoves: number;
  resetFilters: () => void;
  shownCount: number;
  totalCount: number;
}) {
  // Tri-state sort button: off / ↓ (desc) / ↑ (asc). Click cycles.
  const sortBtn = (key: SortKey, label: string) => {
    const active = sortKey === key;
    const arrow = !active ? "↕" : sortDir === "desc" ? "↓" : "↑";
    return (
      <button
        key={key}
        onClick={() => cycleSort(key)}
        style={{
          background: active ? p.accent : p.card,
          color: active ? p.accentFg : p.fg,
          border: `2px solid ${p.border}`,
          padding: "5px 10px",
          fontFamily: TYPE.mono,
          fontWeight: 800,
          fontSize: 10.5,
          letterSpacing: 0.4,
          textTransform: "uppercase",
          cursor: "pointer",
          opacity: active ? 1 : 0.7,
          display: "inline-flex",
          alignItems: "center",
          gap: 5,
        }}
      >
        {label}
        <span style={{ opacity: active ? 1 : 0.5 }}>{arrow}</span>
      </button>
    );
  };

  const selectStyle: React.CSSProperties = {
    width: "100%",
    background: p.card,
    color: p.fg,
    border: `2px solid ${p.border}`,
    padding: "6px 8px",
    fontFamily: TYPE.mono,
    fontWeight: 700,
    fontSize: 12,
    cursor: "pointer",
  };

  const fieldLabel = (t: string) => (
    <div style={{ fontSize: 9.5, fontFamily: TYPE.mono, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", color: p.fgMuted, marginBottom: 5 }}>
      {t}
    </div>
  );

  // Any filter narrowing the full set?
  const filtersActive =
    selProviders.size > 0 ||
    !!selTimeFormat ||
    !!selOpening ||
    !!selOpponent ||
    accRange[0] > 0 ||
    accRange[1] < 100 ||
    misRange[0] > 0 ||
    misRange[1] < maxMistakes ||
    movesRange[0] > 0 ||
    movesRange[1] < maxMoves;

  return (
    <BrBox padding={isMobile ? 12 : 16} shadowSize={4} style={{ marginBottom: 14 }}>
      {/* Sort row + Filters toggle (right-aligned) */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <span style={{ fontSize: 9.5, fontFamily: TYPE.mono, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", color: p.fgMuted }}>Sort</span>
        {sortBtn("date", "Date")}
        {sortBtn("accuracy", "Accuracy")}
        {sortBtn("mistakes", "Mistakes")}
        {sortBtn("time", "Time")}
        {sortBtn("moves", "Moves")}
        <div style={{ flex: 1 }} />
        <BrBtn size="sm" variant={filtersOpen || filtersActive ? "accent" : "surface"} onClick={() => setFiltersOpen(!filtersOpen)}>
          Filters{filtersActive ? " •" : ""} {filtersOpen ? "▲" : "▼"}
        </BrBtn>
      </div>

      {!filtersOpen ? null : (
      <>
      {/* Filters */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)",
          gap: 14,
          marginTop: 16,
          paddingTop: 16,
          borderTop: `2px solid ${p.border}`,
        }}
      >
        {/* Row 1: platform only (spans full width). */}
        <div style={{ gridColumn: isMobile ? "auto" : "1 / -1" }}>
          {fieldLabel("Platform")}
          <div style={{ display: "flex", gap: 14, flexWrap: "wrap", paddingTop: 2 }}>
            {providers.map((prov) => (
              <label key={prov} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, fontFamily: TYPE.mono, fontWeight: 700, cursor: "pointer", textTransform: "uppercase" }}>
                <input type="checkbox" checked={selProviders.has(prov)} onChange={() => toggleProvider(prov)} style={{ accentColor: p.accent, width: 15, height: 15 }} />
                {prov}
              </label>
            ))}
          </div>
        </div>

        {/* Row 2: dropdowns. */}
        <div>
          {fieldLabel("Time format")}
          <select value={selTimeFormat} onChange={(e) => setSelTimeFormat(e.target.value)} style={selectStyle}>
            <option value="">All</option>
            {timeFormats.map((tc) => (
              <option key={tc} value={tc}>{formatTimeControl(tc)}</option>
            ))}
          </select>
        </div>

        <div>
          {fieldLabel("Opponent")}
          <select value={selOpponent} onChange={(e) => setSelOpponent(e.target.value)} style={selectStyle}>
            <option value="">All</option>
            {opponents.map((o) => (
              <option key={o} value={o}>{o}</option>
            ))}
          </select>
        </div>

        <div>
          {fieldLabel("Opening")}
          <select value={selOpening} onChange={(e) => setSelOpening(e.target.value)} style={selectStyle}>
            <option value="">All</option>
            {openings.map((o) => (
              <option key={o} value={o}>{o}</option>
            ))}
          </select>
        </div>

        {/* Row 3: sliders. */}
        <div>
          {fieldLabel("Accuracy %")}
          <BrRangeSlider min={0} max={100} value={accRange} onChange={setAccRange} format={(n) => `${n}%`} />
        </div>

        <div>
          {fieldLabel("My mistakes")}
          <BrRangeSlider min={0} max={Math.max(1, maxMistakes)} value={misRange} onChange={setMisRange} />
        </div>

        <div>
          {fieldLabel("Moves")}
          <BrRangeSlider min={0} max={Math.max(1, maxMoves)} value={movesRange} onChange={setMovesRange} />
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginTop: 14, flexWrap: "wrap" }}>
        <span style={{ fontSize: 11, fontFamily: TYPE.mono, fontWeight: 700, color: p.fgMuted, letterSpacing: 0.5 }}>
          SHOWING {shownCount} / {totalCount}
        </span>
        <BrBtn size="sm" variant="surface" withShadow={false} onClick={resetFilters}>Reset filters</BrBtn>
      </div>
      </>
      )}
    </BrBox>
  );
}

function Section({
  label,
  title,
  isMobile,
  children,
}: {
  label: string;
  title: string;
  isMobile: boolean;
  children: React.ReactNode;
}) {
  return (
    <div style={{ marginBottom: isMobile ? 24 : 32 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 12, flexWrap: "wrap", gap: 10 }}>
        <div>
          <BrLabel>{label}</BrLabel>
          <h2 style={{ fontFamily: TYPE.display, fontSize: isMobile ? 24 : 30, fontWeight: 800, letterSpacing: -0.8, textTransform: "uppercase", margin: "4px 0 0" }}>{title}</h2>
        </div>
      </div>
      {children}
    </div>
  );
}
